import { getSessionTenantId } from '@/utils/active-tenant';
import axios, { storeCsrfToken } from './utils/axios';

const pathPrefix = '/auth';
const APP_SESSION_PATH = '/__app-session';

/** Put the API session cookie on this app host so /__soulmap-download can auth. */
async function mirrorSessionToAppOrigin(
  action: 'login' | 'logout' | 'sso-session',
  headers: Record<string, string>
): Promise<void> {
  try {
    const response = await fetch(`${APP_SESSION_PATH}?action=${action}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: '{}',
    });
    const contentType = response.headers.get('content-type') ?? '';
    // On logout the API call above already ended the token, so the mirror's
    // upstream call gets 401; the host cookies are still expired.
    const expectedLogout401 = action === 'logout' && response.status === 401;
    if (
      (!response.ok && !expectedLogout401) ||
      contentType.includes('text/html')
    ) {
      console.error(
        'Could not copy the session cookie onto this host',
        response.status
      );
    }
  } catch {
    console.error('Could not copy the session cookie onto this host');
  }
}

function getSessionTenantHeaders(): Record<string, string> | undefined {
  const tenantId = getSessionTenantId();
  return tenantId ? { 'x-tenant-id': tenantId.toString() } : undefined;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
}

export const AuthService = {
  login: async (credentials: { email: string; password: string }) => {
    try {
      credentials.email = credentials.email
        ? credentials.email.toLowerCase()
        : '';
      const { email, password } = credentials;
      const encodedCredentials = btoa(`${email}:${password}`);
      const response = await axios.post(
        `${pathPrefix}/token-auth/login/`,
        {
          // ... request body (if any) ...
        },
        {
          headers: {
            Authorization: `Basic ${encodedCredentials}`,
          },
        }
      );
      storeCsrfToken(response.data.csrf_token);
      await mirrorSessionToAppOrigin('login', {
        'X-Lift-Authorization': `Basic ${encodedCredentials}`,
      });
      return response.data;
    } catch (error) {
      console.error('Login error', error);
      throw error;
    }
  },
  logout: async () => {
    const csrfToken = sessionStorage.getItem('csrf-token');
    try {
      return await axios.post(
        `${pathPrefix}/token-auth/logout/`,
        {},
        {
          headers: getSessionTenantHeaders(),
          skipUnauthorizedRedirect: true,
          suppressErrorToast: true,
        }
      );
    } finally {
      await mirrorSessionToAppOrigin('logout', {
        ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      });
    }
  },
  establishSsoSession: async (accessToken: string, idToken: string) => {
    const response = await axios.post(
      `${pathPrefix}/sso-session/`,
      {},
      {
        headers: {
          Authorization: `Token ${accessToken}`,
          'x-id-token': idToken,
        },
      }
    );
    storeCsrfToken(response.data.csrf_token);
    await mirrorSessionToAppOrigin('sso-session', {
      'X-Lift-Authorization': `Token ${accessToken}`,
      'x-id-token': idToken,
    });
    return response.data;
  },
  getUserPermissions: async () => {
    try {
      const response = await axios.get(`/auth/my-permission/`);
      return response.data;
    } catch (error) {
      console.error('Get user permissions error', error);
      throw error;
    }
  },
  getUserPermissionsById: async (userId: string | number) => {
    try {
      const response = await axios.get(`/auth/user-permission/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Get user permissions error', error);
      throw error;
    }
  },
  updateUserPermissions: async (userId: string | number, permissions: any) => {
    try {
      const response = await axios.put(
        `/auth/user-permission/${userId}/`,
        permissions
      );
      return response.data;
    } catch (error) {
      console.error('Update user permissions error', error);
      throw error;
    }
  },
  bulkImportUsers: async (csvFile: File) => {
    try {
      const formData = new FormData();
      formData.append('csv_file', csvFile);

      const response = await axios.post(
        `${pathPrefix}/bulk-user-import/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Bulk import users error', error);
      throw error;
    }
  },

  /**
   * Change user's password
   */
  changePassword: async (data: ChangePasswordRequest) => {
    try {
      const response = await axios.post(`${pathPrefix}/change-password/`, {
        ...data,
        send_email: true,
      });
      return response.data;
    } catch (error) {
      console.error('Change password error', error);
      throw error;
    }
  },
};
